// US-074 / US-075 — AI draft surface wrapper.
//
// Extracted verbatim from `ThreadPanel` as part of the audit-cleanup
// structural split. Pure presentational: it renders the `<ol>` that holds
// either the auto-mode preview bubble or the review-mode editor. The parent
// keeps the mount/visibility guard (`!aiDisabled && draftBody && …`) so the
// US-074 race fix (dropping the surface once auto-mode has posted) stays
// owned where the draft state lives.
//
// Mounts the same `<DraftStreamLiveRegion>` the extension's in-page panel
// uses (`@barel/ui` `DraftPanel` composite) so assistive tech announces the
// streaming AI draft here too — the web surface previously had no aria-live
// region for it.

import { DraftStreamLiveRegion } from '@barel/ui';

import { AutoPreview } from './AutoPreview';
import { ReviewEditor } from './ReviewEditor';
import type { DraftStreamError } from './useDraftStream';
import type { AiMode } from './types';

export interface AiDraftSurfaceProps {
  aiMode: AiMode;
  hotOverride: boolean;
  draftBody: string;
  draftSent: boolean;
  onChange: (next: string) => void;
  onSend: () => void;
  onRegenerate: () => void;
  streaming: boolean;
  sending: boolean;
  serverError: DraftStreamError | null;
  onUpgrade?: () => void;
}

export function AiDraftSurface({
  aiMode,
  hotOverride,
  draftBody,
  draftSent,
  onChange,
  onSend,
  onRegenerate,
  streaming,
  sending,
  serverError,
  onUpgrade,
}: AiDraftSurfaceProps) {
  return (
    <div
      data-ls-region="ai-draft-surface"
      style={{
        padding: '0 var(--space-4) var(--space-3)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
      }}
    >
      <DraftStreamLiveRegion text={draftBody} streaming={streaming} />
      <ol
        aria-label="ai draft surface"
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
        }}
      >
        {aiMode === 'auto' && !hotOverride ? (
          // US-074 race fix — the parent guard hides this branch entirely
          // once `draftSent` flips, so by the time we get here the
          // AutoPreview is always rendering an in-flight (sending) bubble.
          <AutoPreview body={draftBody} sent={draftSent} />
        ) : (
          <ReviewEditor
            body={draftBody}
            onChange={onChange}
            onSend={onSend}
            onRegenerate={onRegenerate}
            streaming={streaming}
            sending={sending}
            serverError={serverError}
            onUpgrade={onUpgrade}
          />
        )}
      </ol>
    </div>
  );
}
